import { useLayoutEffect, useRef, useState } from 'react'

const endOf = element => element.offsetLeft + element.offsetWidth

// Reads the hidden measure copy (every item, then a "+N" counter) and returns how
// many leading items fit the row, keeping room for the counter whenever some are
// left out. The first item always shows, even when it alone overflows.
const countFitting = (row, measure, itemCount) => {
  const style = window.getComputedStyle(row)
  const padding = (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0)
  const available = row.clientWidth - padding
  const items = [...measure.querySelectorAll('[data-measure-item]')]
  const counter = measure.querySelector('[data-measure-counter]')
  const counterWidth = endOf(counter) - endOf(items[items.length - 1])

  for (let count = itemCount; count > 1; count -= 1) {
    const needed = endOf(items[count - 1]) + (count < itemCount ? counterWidth : 0)
    if (needed <= available) {
      return count
    }
  }

  return 1
}

/**
 * Tracks how many of the row's items fit its width, re-measuring whenever the row
 * is resized (e.g. while the detached window is stretched) or its text changes.
 * @param {number} itemCount - Number of items the row can show
 * @param {string} text - The items' text, so a content change re-measures
 * @returns {[Object, Object, number]} - Refs for the row and its measure copy, and
 * the number of items to show
 */
function useVisibleCount(itemCount, text) {
  const rowRef = useRef(null)
  const measureRef = useRef(null)
  const [visibleCount, setVisibleCount] = useState(itemCount)

  useLayoutEffect(() => {
    const row = rowRef.current
    const measure = measureRef.current
    if (!row || !measure || itemCount === 0) {
      return undefined
    }

    const update = () => setVisibleCount(countFitting(row, measure, itemCount))
    update()

    if (typeof ResizeObserver === 'undefined') {
      return undefined
    }

    const observer = new ResizeObserver(update)
    observer.observe(row)

    return () => observer.disconnect()
  }, [itemCount, text])

  return [rowRef, measureRef, visibleCount]
}

export default useVisibleCount
